import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Table, Modal, Button, Input, Select, message, Tag, Tooltip, Row, Col, Popconfirm } from 'antd';
import {
  TeamOutlined, UserAddOutlined, EditOutlined, KeyOutlined,
  ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined,
  PlusOutlined, DeleteOutlined,
} from '@ant-design/icons';
import {
  getTeleradiologyMembers,
  createTeleradiologyMember,
  updateMemberGroup,
  resetMemberPassword,
  getTeleradiologyGroups,
  createTeleradiologyGroup,
  updateTeleradiologyGroup,
  deleteTeleradiologyGroup,
} from './TeleradiologyService';

const { Option } = Select;
const TEAL = '#2a5f5a';

const DEFAULT_GROUPS = [
  { id: 'group_a', name: 'Group A — General Radiology',        color: '#2563eb' },
  { id: 'group_b', name: 'Group B — Neuroradiology/Specialist', color: '#7c3aed' },
  { id: 'group_c', name: 'Group C — On-Call / STAT',           color: '#dc2626' },
];

const GROUP_COLORS = ['#2563eb','#7c3aed','#dc2626','#16a34a','#d97706','#0891b2','#db2777','#65a30d'];

const GroupTag = ({ groupId, groups }) => {
  const g = (groups || DEFAULT_GROUPS).find(x => x.id === groupId);
  if (!g) return <Tag color="default">{groupId || 'No Group'}</Tag>;
  return <Tag color={g.color}>{(g.name || g.id).split(' — ')[0]}</Tag>;
};

const TeleradiologyTeam = ({ profile }) => {
  const roles      = profile?.realm_roles || [];
  const hospitalId = profile?.hospital_id  || '';
  const isTele     = hospitalId === 'teleradiology';
  const isCoord    = isTele && roles.some(r => ['PO', 'PROJECT_OWNER', 'MANAGER', 'ADMIN'].includes(r));

  const [members, setMembers]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [groups, setGroups]     = useState(DEFAULT_GROUPS);

  /* ── Group management modal ── */
  const [groupsModalOpen, setGroupsModalOpen] = useState(false);
  const [addGroupForm, setAddGroupForm]       = useState({ name: '', color: GROUP_COLORS[0] });
  const [addingGroup, setAddingGroup]         = useState(false);
  const [editGroupData, setEditGroupData]     = useState(null); // { id, name, color }
  const [savingGroup, setSavingGroup]         = useState(false);

  /* ── Add member modal ── */
  const [addOpen, setAddOpen]   = useState(false);
  const [adding, setAdding]     = useState(false);
  const [addForm, setAddForm]   = useState({ username: '', firstName: '', lastName: '', email: '', groupId: 'group_a', role: 'ANNOTATOR', password: '' });

  /* ── Edit group modal ── */
  const [editOpen, setEditOpen] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [editGroupId, setEditGroupId] = useState('');
  const [editSaving, setEditSaving]   = useState(false);

  /* ── Reset password modal ── */
  const [pwOpen, setPwOpen]     = useState(false);
  const [pwMember, setPwMember] = useState(null);
  const [newPw, setNewPw]       = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const loadGroups = useCallback(() => {
    getTeleradiologyGroups()
      .then(({ data }) => { if (data?.data?.length) setGroups(data.data); })
      .catch(() => {});
  }, []);

  const loadMembers = useCallback(() => {
    setLoading(true);
    getTeleradiologyMembers()
      .then(({ data }) => setMembers(data?.data || []))
      .catch(() => message.error('Failed to load team'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (isCoord) { loadGroups(); loadMembers(); }
  }, [isCoord, loadGroups, loadMembers]);

  /* ── Group handlers ── */
  const handleAddGroup = async () => {
    if (!addGroupForm.name.trim()) { message.warning('Group name is required'); return; }
    setAddingGroup(true);
    try {
      const { data } = await createTeleradiologyGroup({ name: addGroupForm.name.trim(), color: addGroupForm.color });
      message.success(`Group "${data.name}" created`);
      setAddGroupForm({ name: '', color: GROUP_COLORS[0] });
      loadGroups();
    } catch (e) {
      message.error('Failed to create group');
    } finally { setAddingGroup(false); }
  };

  const handleSaveGroup = async () => {
    if (!editGroupData?.name?.trim()) { message.warning('Name required'); return; }
    setSavingGroup(true);
    try {
      await updateTeleradiologyGroup(editGroupData.id, { name: editGroupData.name.trim(), color: editGroupData.color });
      message.success('Group updated');
      setEditGroupData(null);
      loadGroups();
    } catch (e) {
      message.error('Failed to update group');
    } finally { setSavingGroup(false); }
  };

  const handleDeleteGroup = async (id) => {
    try {
      await deleteTeleradiologyGroup(id);
      message.success('Group deleted');
      loadGroups();
    } catch (e) {
      message.error('Failed to delete group');
    }
  };

  /* ── Stats ── */
  const radiologists = members.filter(m => m.roles?.includes('ANNOTATOR'));
  const coordinators = members.filter(m => m.roles?.some(r => ['PO', 'PROJECT_OWNER'].includes(r)));
  const byGroup = groups.map(g => ({
    ...g,
    members: radiologists.filter(m => m.group_id === g.id),
  }));

  /* ── Handlers ── */
  const handleAdd = async () => {
    if (!addForm.username || !addForm.password) { message.warning('Username and password are required'); return; }
    setAdding(true);
    try {
      const resp = await createTeleradiologyMember({
        username:   addForm.username,
        first_name: addForm.firstName,
        last_name:  addForm.lastName,
        email:      addForm.email,
        group_id:   addForm.groupId,
        role:       addForm.role,
        password:   addForm.password,
      });
      // API returns 200 even for known errors to bypass axios interceptor.
      if (resp.data?.already_exists) {
        message.warning(resp.data.message || 'Username or email already in use');
        setAdding(false);
        return;
      }
      if (resp.data?.password_error || resp.data?.create_error) {
        message.error(resp.data.message || 'Failed to create member');
        setAdding(false);
        return;
      }
      message.success(`${addForm.username} added to the team`);
      setAddOpen(false);
      setAddForm({ username: '', firstName: '', lastName: '', email: '', groupId: 'group_a', role: 'ANNOTATOR', password: '' });
      loadMembers();
    } catch (e) {
      message.error('Unexpected error — please check the username and try again');
    } finally {
      setAdding(false);
    }
  };

  const handleEditGroup = async () => {
    setEditSaving(true);
    try {
      await updateMemberGroup(editMember.id, editGroupId);
      message.success(`${editMember.username} moved to ${groups.find(g => g.id === editGroupId)?.name || editGroupId}`);
      setEditOpen(false);
      loadMembers();
    } catch (e) {
      message.error('Failed to update group');
    } finally {
      setEditSaving(false);
    }
  };

  const handleResetPw = async () => {
    if (!newPw || newPw.length < 6) { message.warning('Password must be at least 6 characters'); return; }
    setPwSaving(true);
    try {
      await resetMemberPassword(pwMember.id, newPw);
      message.success(`Password reset for ${pwMember.username} — they must change it on next login`);
      setPwOpen(false);
      setNewPw('');
    } catch (e) {
      message.error('Failed to reset password');
    } finally {
      setPwSaving(false);
    }
  };

  if (!isCoord) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: '#6b7280' }}>
        <TeamOutlined style={{ fontSize: 48, marginBottom: 12 }} />
        <p>Only dispatch coordinators can manage team members.</p>
      </div>
    );
  }

  const columns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      render: (v, r) => (
        <div>
          <span style={{ fontWeight: 600, color: '#111827' }}>{v}</span>
          {r.first_name || r.last_name ? (
            <div style={{ fontSize: 12, color: '#6b7280' }}>{[r.first_name, r.last_name].filter(Boolean).join(' ')}</div>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: v => v || <span style={{ color: '#d1d5db' }}>—</span>,
    },
    {
      title: 'Role',
      key: 'role',
      width: 160,
      render: (_, r) => {
        const isPO = r.roles?.some(x => ['PO', 'PROJECT_OWNER'].includes(x));
        return <Tag color={isPO ? TEAL : '#2563eb'}>{isPO ? 'Coordinator' : 'Radiologist'}</Tag>;
      },
    },
    {
      title: 'Group',
      key: 'group',
      width: 180,
      render: (_, r) => <GroupTag groupId={r.group_id} groups={groups} />,
    },
    {
      title: 'Status',
      key: 'status',
      width: 90,
      render: (_, r) => r.enabled
        ? <span style={{ color: '#16a34a', fontSize: 12 }}><CheckCircleOutlined /> Active</span>
        : <span style={{ color: '#dc2626', fontSize: 12 }}><CloseCircleOutlined /> Disabled</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      render: (_, r) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Tooltip title="Change group">
            <Button size="small" icon={<EditOutlined />} onClick={() => {
              setEditMember(r);
              setEditGroupId(r.group_id || 'group_a');
              setEditOpen(true);
            }} />
          </Tooltip>
          <Tooltip title="Reset password">
            <Button size="small" icon={<KeyOutlined />} onClick={() => {
              setPwMember(r);
              setNewPw('');
              setPwOpen(true);
            }} />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: '12px 20px', background: '#f8fafc', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#111827' }}>
          <TeamOutlined style={{ marginRight: 8, color: TEAL }} />
          Team Management
        </h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button size="small" icon={<ReloadOutlined />} onClick={loadMembers}>Refresh</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => setGroupsModalOpen(true)}>
            Manage Groups
          </Button>
          <Button type="primary" size="small" icon={<UserAddOutlined />} style={{ background: TEAL, borderColor: TEAL }}
            onClick={() => setAddOpen(true)}>
            Add Radiologist
          </Button>
        </div>
      </div>

      {/* Group summary chips */}
      <Row gutter={10} style={{ marginBottom: 12 }}>
        {byGroup.map(g => (
          <Col key={g.id} span={Math.max(6, Math.floor(24 / Math.max(byGroup.length, 1)))}>
            <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 22, fontWeight: 700, color: g.color || '#6b7280' }}>{g.members.length}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{(g.name || g.id).split(' — ')[0]}</div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>{(g.name || '').split(' — ')[1] || ''}</div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* Members table */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <Table
          columns={columns}
          dataSource={members}
          rowKey="id"
          loading={loading}
          size="middle"
          pagination={{ pageSize: 15, showTotal: t => `${t} members` }}
          locale={{ emptyText: 'No team members yet — add your first radiologist' }}
        />
      </div>

      {/* ── Add member modal ── */}
      <Modal
        title={<span><UserAddOutlined style={{ marginRight: 8, color: TEAL }} />Add Radiologist</span>}
        open={addOpen}
        onCancel={() => !adding && setAddOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setAddOpen(false)} disabled={adding}>Cancel</Button>,
          <Button key="add" type="primary" loading={adding} onClick={handleAdd}
            style={{ background: TEAL, borderColor: TEAL }}>Add Member</Button>,
        ]}
        width={520}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Username <span style={{ color: '#dc2626' }}>*</span></div>
            <Input value={addForm.username} onChange={e => setAddForm(f => ({ ...f, username: e.target.value }))} placeholder="dr_firstname" />
          </div>
          <Row gutter={10}>
            <Col span={12}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>First Name</div>
              <Input value={addForm.firstName} onChange={e => setAddForm(f => ({ ...f, firstName: e.target.value }))} placeholder="First" />
            </Col>
            <Col span={12}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Last Name</div>
              <Input value={addForm.lastName} onChange={e => setAddForm(f => ({ ...f, lastName: e.target.value }))} placeholder="Last" />
            </Col>
          </Row>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Email</div>
            <Input value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} placeholder="dr@hospital.com" />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Group</div>
            <Select value={addForm.groupId} onChange={v => setAddForm(f => ({ ...f, groupId: v }))} style={{ width: '100%' }}>
              {groups.map(g => <Option key={g.id} value={g.id}>{g.name || g.id}</Option>)}
            </Select>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Temporary Password <span style={{ color: '#dc2626' }}>*</span></div>
            <Input.Password value={addForm.password} onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))} placeholder="Min 8 characters" />
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>User must change this on first login.</div>
          </div>
        </div>
      </Modal>

      {/* ── Edit group modal ── */}
      <Modal
        title={<span><EditOutlined style={{ marginRight: 8 }} />Change Group — {editMember?.username}</span>}
        open={editOpen}
        onCancel={() => !editSaving && setEditOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setEditOpen(false)} disabled={editSaving}>Cancel</Button>,
          <Button key="save" type="primary" loading={editSaving} onClick={handleEditGroup}
            style={{ background: TEAL, borderColor: TEAL }}>Save</Button>,
        ]}
        width={400}
      >
        <div style={{ marginBottom: 8, fontWeight: 500 }}>Assign to group:</div>
        <Select value={editGroupId} onChange={setEditGroupId} style={{ width: '100%' }}>
          {groups.map(g => <Option key={g.id} value={g.id}>{g.name || g.id}</Option>)}
        </Select>
      </Modal>

      {/* ── Reset password modal ── */}
      <Modal
        title={<span><KeyOutlined style={{ marginRight: 8 }} />Reset Password — {pwMember?.username}</span>}
        open={pwOpen}
        onCancel={() => !pwSaving && setPwOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setPwOpen(false)} disabled={pwSaving}>Cancel</Button>,
          <Button key="reset" type="primary" danger loading={pwSaving} onClick={handleResetPw}>Reset Password</Button>,
        ]}
        width={400}
      >
        <div style={{ marginBottom: 8, fontWeight: 500 }}>New temporary password:</div>
        <Input.Password value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="Min 6 characters" />
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
          User must change this on next login.
        </div>
      </Modal>

      {/* ── Manage Groups modal ── */}
      <Modal
        title={<span><EditOutlined style={{ marginRight: 8, color: TEAL }} />Manage Radiologist Groups</span>}
        open={groupsModalOpen}
        onCancel={() => setGroupsModalOpen(false)}
        footer={<Button onClick={() => setGroupsModalOpen(false)}>Close</Button>}
        width={520}
      >
        {/* Existing groups */}
        <div style={{ marginBottom: 16 }}>
          {groups.map(g => (
            <div key={g.id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 12px', borderRadius: 8, marginBottom: 6,
              background: '#f9fafb', border: '1px solid #e5e7eb',
            }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: g.color || '#6b7280', flexShrink: 0 }} />
              {editGroupData?.id === g.id ? (
                <>
                  <Input
                    size="small"
                    value={editGroupData.name}
                    onChange={e => setEditGroupData(d => ({ ...d, name: e.target.value }))}
                    style={{ flex: 1 }}
                    autoFocus
                  />
                  <Select size="small" value={editGroupData.color} onChange={c => setEditGroupData(d => ({ ...d, color: c }))} style={{ width: 80 }}>
                    {GROUP_COLORS.map(c => (
                      <Option key={c} value={c}>
                        <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: c, marginRight: 4 }} />
                      </Option>
                    ))}
                  </Select>
                  <Button size="small" type="primary" loading={savingGroup} onClick={handleSaveGroup}
                    style={{ background: TEAL, borderColor: TEAL }}>Save</Button>
                  <Button size="small" onClick={() => setEditGroupData(null)}>Cancel</Button>
                </>
              ) : (
                <>
                  <span style={{ flex: 1, fontWeight: 500, fontSize: 13 }}>{g.name || g.id}</span>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>{g.id}</span>
                  <Button size="small" icon={<EditOutlined />}
                    onClick={() => setEditGroupData({ id: g.id, name: g.name, color: g.color || '#2563eb' })} />
                  <Popconfirm
                    title="Delete this group? Members will need to be reassigned."
                    onConfirm={() => handleDeleteGroup(g.id)}
                    okText="Delete" cancelText="Cancel" okType="danger"
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Add new group */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Add New Group</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Input
              placeholder="Group name (e.g. Chest Radiology)"
              value={addGroupForm.name}
              onChange={e => setAddGroupForm(f => ({ ...f, name: e.target.value }))}
              style={{ flex: 1 }}
            />
            <Select value={addGroupForm.color} onChange={c => setAddGroupForm(f => ({ ...f, color: c }))} style={{ width: 70 }}>
              {GROUP_COLORS.map(c => (
                <Option key={c} value={c}>
                  <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: c }} />
                </Option>
              ))}
            </Select>
            <Button type="primary" icon={<PlusOutlined />} loading={addingGroup} onClick={handleAddGroup}
              style={{ background: TEAL, borderColor: TEAL }}>Add</Button>
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
            The group ID is auto-generated from the name and cannot be changed later.
          </div>
        </div>
      </Modal>

    </div>
  );
};

const mapStateToProps = (state) => ({ profile: state.system.profile });
export default connect(mapStateToProps)(withRouter(TeleradiologyTeam));
